import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { handleLineItemClick } from "../utils/lineItemNavigation";

function ReportRow({ item, level = 0, months = [], onHide, isTrialBalance = false }) {
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    setExpanded(!item.defaultCollapsedWhenCondensed);
  }, [item.defaultCollapsedWhenCondensed]);

  const isMoM = months.length > 0;
  const hasChildren = item.children && item.children.length > 0;
  const navigate = useNavigate();

  // Normalize all possible data shapes (MoM, non-MoM, combined)
  const getNodeAmounts = (node) => {
    if (!node) return {};

    // Month-over-month data (object of months)
    if (
      node.months &&
      typeof node.months === "object" &&
      Object.keys(node.months).length > 0
    ) {
      return node.months;
    }

    // Non-MoM single value
    if (node.displayAmount !== undefined && node.displayAmount !== null) {
      return { Amount: node.displayAmount };
    }

    // Alternate key some backends may use
    if (
      node.amounts &&
      typeof node.amounts === "object" &&
      Object.keys(node.amounts).length > 0
    ) {
      return node.amounts;
    }

    return {};
  };

  const itemAmounts = getNodeAmounts(item);
  const isConsolidated = item.name?.toLowerCase?.().includes("consolidated");

  // helper for company root (non-combined MoM)
  const isNonCombinedMoMCompanyRoot = isMoM && level === 0 && !isConsolidated;

  // Recursive total summation for children
  const sumChildren = (children) => {
    if (!children || children.length === 0) return {};
    return children.reduce((acc, c) => {
      const cAmounts = getNodeAmounts(c);
      const keys = months.length > 0 ? months : Object.keys(cAmounts);
      keys.forEach((m) => {
        const num = Number(cAmounts[m]) || 0;
        acc[m] = (acc[m] || 0) + num;
      });
      const childSums = sumChildren(c.children);
      keys.forEach((m) => {
        acc[m] = (acc[m] || 0) + (childSums[m] || 0);
      });
      return acc;
    }, {});
  };

  // Sum ONLY immediate children's MoM totals per month (no recursion)
  const sumImmediateChildrenMonths = (children, monthsList) => {
    const acc = {};
    if (!children || children.length === 0) return acc;

    children.forEach((c) => {
      const cMonths = c.months ?? c.monthsAmount ?? {};
      const keys = monthsList.length > 0 ? monthsList : Object.keys(cMonths);
      keys.forEach((m) => {
        acc[m] = (acc[m] || 0) + (Number(cMonths[m]) || 0);
      });
    });

    return acc;
  };

  const consolidatedTotals = isConsolidated ? sumChildren(item.children) : null;

  // Compute horizontal total across all columns (sum across months or Amounts)
  const columnKeys = months.length > 0 ? months : Object.keys(itemAmounts);

  // Use consolidatedTotals if this is a consolidated row that’s collapsed
  const baseForTotal =
    isConsolidated && !expanded && consolidatedTotals
      ? consolidatedTotals
      : itemAmounts;

  const horizontalTotal = columnKeys.reduce(
    (sum, key) => sum + (Number(baseForTotal[key]) || 0),
    0
  );

  // force 0 for display on top-level non-combined MoM companies
  let horizontalTotalForDisplay = isNonCombinedMoMCompanyRoot ? 0 : horizontalTotal;

  // Currency formatting with dash / blank logic
  const formatCurrency = (value, showDash = true) => {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(value);
    if (isNaN(num)) return "";

    // Special rule: non-combined MoM top-level companies show nothing for zeros
    if (isNonCombinedMoMCompanyRoot && num === 0) return "";

    if (num === 0 && showDash) return "–";
    if (num === 0 && !showDash) return "";
    return num.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    });
  };

  const getTrialBalanceValues = (node) => {
    if (node.debit !== undefined || node.credit !== undefined) {
      return {
        debit: Number(node.debit || 0),
        credit: Number(node.credit || 0),
      };
    }

    const amount = Number(node.amount ?? node.value ?? node.displayAmount ?? 0);

    if (node.balanceType === "DEBIT") {
      return { debit: amount, credit: 0 };
    }

    if (node.balanceType === "CREDIT") {
      return { debit: 0, credit: amount };
    }

    return { debit: 0, credit: 0 };
  };

  // Positive / negative styling
  const getColorClass = (val) => {
    const num = Number(val);
    if (isNaN(num)) return "";
    if (num < 0) return "negative";
    if (num > 0) return "positive";
    return "";
  };

  // Determine which values to show (empty if expanded)
  let displayAmounts;
  let showDashes = true;

  if (hasChildren) {
    if (expanded) {
      if (isMoM) {
        // MoM: hide numbers on expanded parents for a clean hierarchy
        const keys =
          months.length > 0
            ? months
            : Object.keys(item.months ?? item.monthsAmount ?? {});

        if (isNonCombinedMoMCompanyRoot) {
          // Fix: backend 0.0 should display as $0.00
          displayAmounts = keys.reduce((acc, m) => {
            acc[m] = 0;
            return acc;
          }, {});
          showDashes = true;
        } else {
          const totals = item.months ?? item.monthsAmount ?? {};
          const kidsSum = sumImmediateChildrenMonths(item.children, keys);

          displayAmounts = keys.reduce((acc, m) => {
            let own = (Number(totals[m]) || 0) - (Number(kidsSum[m]) || 0);
            if (Math.abs(own) < 0.0001) own = 0;
            acc[m] = own === 0 ? "" : own;
            return acc;
          }, {});
          showDashes = false;
        }
      } else {
        // Non-MoM: expanded parent rows should NOT display numbers
        displayAmounts = { Amount: "" };
        showDashes = false;
      }
    } else {
      // Collapsed: show total (value + children)
      if (!isMoM) {
        displayAmounts = { Amount: item.displayAmount ?? "" };
      } else {
        displayAmounts = isConsolidated ? consolidatedTotals : itemAmounts;
      }
      showDashes = true;
    }
  } else {
    // Leaf: show its own number as usual
    displayAmounts = itemAmounts;
    showDashes = true;
  }

  // if our existing horizontal total is zero, but the row is actually showing non-zero values in its cells,
  // recompute the total directly from displayAmounts (this fixes combined MoM parents)
  const displayKeys =
    months.length > 0 ? months : Object.keys(displayAmounts || {});
  const totalFromDisplay = displayKeys.reduce(
    (sum, key) => sum + (Number(displayAmounts?.[key]) || 0),
    0
  );
  const anyNonZeroDisplayed = displayKeys.some(
    (key) => Math.abs(Number(displayAmounts?.[key]) || 0) > 0.0001
  );

  if (
    anyNonZeroDisplayed &&
    Math.abs(horizontalTotalForDisplay) < 0.0001 &&
    Math.abs(totalFromDisplay) > 0.0001
  ) {
    horizontalTotalForDisplay = totalFromDisplay;
  }

  const totalsAmounts = isConsolidated
    ? consolidatedTotals
    : getNodeAmounts(item.totals || item);

  // Conditional flag for showing Total column
  const showTotalColumn = displayKeys.length > 1;

  // Conditional logic for horizontal total display
  const shouldShowHorizontalTotal =
    !hasChildren || !expanded; // only show when row is collapsed OR leaf
  
  const canHide =
    !isMoM &&                
    !hasChildren &&
    !isConsolidated &&
    typeof onHide === "function";

  if (isTrialBalance) {
    const { debit, credit } = getTrialBalanceValues(item);

    return (
      <>
        <tr>
          <td style={{ paddingLeft: `${level * 20 + 12}px` }}>
            {hasChildren && (
              <span
                className={`expand-icon ${expanded ? "expanded" : ""}`}
                onClick={() => setExpanded(!expanded)}
                style={{ cursor: "pointer", display: "inline-block", marginRight: 4 }}
              >
                ▶
              </span>
            )}

            <span className="row-name-text">{item.name}</span>
          </td>

          <td style={{ textAlign: "right" }}>
            {debit ? formatCurrency(debit, true) : "–"}
          </td>

          <td style={{ textAlign: "right" }}>
            {credit ? formatCurrency(credit, true) : "–"}
          </td>
        </tr>

        {hasChildren && expanded && (
          <>
            {item.children.map((child, idx) => (
              <ReportRow
                key={child.id ?? idx}
                item={child}
                level={level + 1}
                months={months}
                onHide={onHide}
                isTrialBalance={isTrialBalance}
              />
            ))}
          </>
        )}
      </>
    );
  }

  // Render row(s)
  return (
    <>
      {/* Parent or single row */}
      <tr>
        <td style={{ paddingLeft: `${level * 20 + 12}px` }}>
          {hasChildren && (
            <span
              className={`expand-icon ${expanded ? "expanded" : ""}`}
              onClick={() => setExpanded(!expanded)}
              style={{ cursor: "pointer", display: "inline-block", marginRight: 4 }}
            >
              ▶
            </span>
          )}

          <span className="row-name-text">{item.name}</span>

          {canHide && (
            <button
              className="remove-row-btn"
              onClick={() => onHide(item.id)}
              title="Hide this row"
              style={{ marginLeft: 6 }}
            >
              ✕
            </button>
          )}
        </td>

        {(months.length > 0 ? months : ["Amount"]).map((month) => {
          const value = displayAmounts?.[month];
          const isClickable = !hasChildren && value !== "" && value != null;

          return (
            <td
              key={month}
              className={`${getColorClass(value)} ${
                isClickable ? "clickable" : ""
              }`}
              style={{ textAlign: "right", cursor: isClickable ? "pointer" : "default" }}
              onClick={() => {
                if (!isClickable) return;

                handleLineItemClick({
                  quickbooksItemId: item.quickbooksItemId ?? -1,
                  companyId: item.companyId ?? null,
                  amount: value,
                  month,
                  navigate,
                });
              }}
            >
              {formatCurrency(value, showDashes)}
            </td>
          );
        })}

        {/* Horizontal total column */}
        {showTotalColumn && (
          <td
            className={`total-cell ${getColorClass(horizontalTotalForDisplay)}`}
            style={{
              textAlign: "right",
              fontWeight: "bold",
            }}
          >
            {shouldShowHorizontalTotal
              ? formatCurrency(horizontalTotalForDisplay, true)
              : ""}
          </td>
        )}
      </tr>

      {/* Child rows when expanded */}
      {hasChildren && expanded && (
        <>
          {item.children.map((child, idx) => (
            <ReportRow
              key={child.id ?? idx}
              item={child}
              level={level + 1}
              months={months}
              onHide={onHide}   
            />
          ))}

          {/* Totals row */}
          <tr className="total-row">
            <td
              style={{
                paddingLeft: `${(level + 1) * 14}px`,
                fontWeight: "bold",
              }}
            >
              Total for {item.name}:
            </td>
            {(months.length > 0 ? months : ["Amount"]).map((m) => (
              <td
                key={m}
                className={getColorClass(totalsAmounts?.[m])}
                style={{
                  textAlign: "right",
                  fontWeight: "bold",
                }}
              >
                {formatCurrency(totalsAmounts?.[m], true)}
              </td>
            ))}
            {/* Total cell for total rows */}
            {showTotalColumn && (
              <td className="total-cell total-summary">
                {formatCurrency(
                  displayKeys.reduce(
                    (sum, m) => sum + (Number(totalsAmounts?.[m]) || 0),
                    0
                  ),
                  true
                )}
              </td>
            )}
          </tr>
        </>
      )}
    </>
  );
}

export default ReportRow;
